import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CardContent } from "@/components/ui/card";
import GlowCard from "@/components/GlowCard";
import OptimizedImage from "@/components/OptimizedImage";
import { SimpleReveal } from "@/hooks/useSimpleAnimation";

interface ManufacturingCardProps {
  title: string;
  brand: string;
  description: string;
  imageUrl: string;
  altText: string;
  isMobile?: boolean;
  delay?: number;
}

const ManufacturingCard = ({
  title, 
  brand, 
  description, 
  imageUrl, 
  altText, 
  isMobile = false,
  delay = 0
}: ManufacturingCardProps) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate('/manufacturing');
  };

  const content = (
    <div className="h-full cursor-pointer" onClick={handleClick}>
      <GlowCard 
        className="group/feature h-full flex flex-col transition-transform duration-300 hover:scale-[1.02]" 
        rounded="xl"
      >
        <div className="relative bg-neutral-900 p-8 flex items-center justify-center h-48 w-full overflow-hidden rounded-t-xl">
          <OptimizedImage 
            src={imageUrl}
            alt={altText}
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 ease-out group-hover/feature:scale-[1.03]"
            sizes="400px" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/70 via-neutral-900/60 to-neutral-900/80 transition-all duration-500 group-hover/feature:from-neutral-900/50 group-hover/feature:via-neutral-900/40 group-hover/feature:to-neutral-900/60"></div>
          <div className="relative z-20 flex flex-col items-center justify-center transform transition-transform duration-500 group-hover/feature:scale-105">
            <h3 className="text-2xl font-semibold text-white mb-3 transition-all duration-300 text-center drop-shadow-lg tracking-tight">
              {brand}
            </h3>
            <div className="w-16 h-0.5 bg-gradient-to-r from-[hsl(var(--color-1))] to-[hsl(var(--color-2))] mb-3 transition-all duration-500 group-hover/feature:w-20 drop-shadow-md"></div>
            <p className="text-white/90 text-base font-medium transition-colors duration-300 group-hover/feature:text-white drop-shadow-lg">
              {title}
            </p>
          </div>
        </div>
        
        <CardContent className={`p-6 bg-white rounded-b-xl ${isMobile ? 'text-center' : ''}`}>
          <p className="text-body">
            {description}
          </p>
        </CardContent>
      </GlowCard>
    </div>
  );

  return <SimpleReveal delay={isMobile ? Math.min(delay, 50) : delay}>{content}</SimpleReveal>;
};

ManufacturingCard.displayName = 'ManufacturingCard';

export default React.memo(ManufacturingCard);
